/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useSelector } from 'react-redux';

import type { RecommendationsExplainerInput } from '@coursera-web/recommendations-common';
import { RecommendationsExplainerTitleRow } from '@coursera-web/recommendations-explainer';
import { debounce, isEqual } from 'lodash';

import { isAuthenticatedUser } from 'js/lib/user';

import { Autocomplete, Button, IconButton, breakpoints, typography2, useMediaQuery } from '@coursera/cds-core';
import { AIGenerateBrandedNewIcon, ArrowUpIcon } from '@coursera/cds-icons';

import SpeechToTextButton from 'bundles/ai-coach-platform/components/patterns/chat/common/SpeechToTextButton';
import type { GlobalState } from 'bundles/ai-coach-platform/components/patterns/chat/store/types';
import useBuildOnboardingUrl from 'bundles/onboarding-common/hooks/useBuildOnboardingUrl';
import AutocompleteAiPromptOption from 'bundles/search-common/components/searchbar/autocomplete/AutocompleteAiPromptOption';
import AutocompleteItemIcon from 'bundles/search-common/components/searchbar/autocomplete/AutocompleteItemIcon';
import AutocompleteOnboardingOption from 'bundles/search-common/components/searchbar/autocomplete/AutocompleteOnboardingOption';
import AutocompleteOptionTrackingDiv from 'bundles/search-common/components/searchbar/autocomplete/AutocompleteOptionTrackingDiv';
import AutocompleteRecentlySearchedOption from 'bundles/search-common/components/searchbar/autocomplete/AutocompleteRecentlySearchedOption';
import AutocompleteSearchProductCard from 'bundles/search-common/components/searchbar/autocomplete/AutocompleteSearchProductCard';
import AutocompleteTrendingOption from 'bundles/search-common/components/searchbar/autocomplete/AutocompleteTrendingOption';
import type { EnterpriseSearchBarConfig } from 'bundles/search-common/components/searchbar/searchBarWrapperTypes';
import { SEARCH_MAIN_INDEX_ID, SEARCH_SUGGESTIONS_INDEX_ID } from 'bundles/search-common/constants';
import {
  AI_PROMPTS_ZERO_STATE_GROUP_ID,
  AUTOCOMPLETE_MODES,
  AUTOCOMPLETE_SECTION_PREFIXES,
  type AutocompleteMode,
  DEBOUNCE_DURATION_MS,
  DIRECT_MATCHES_GROUP_ID,
  ENTERPRISE_SKILLS_GROUP_ID,
  LEARNER_FAVORITES_GROUP_ID,
  ONBOARDING_GROUP_ID,
  RECENTLY_VIEWED_GROUP_ID,
  RECENT_SEARCHES_GROUP_ID,
  TRENDING_SEARCHES_GROUP_ID,
  TRENDING_SEARCH_RESULTS_CONFIG,
  TRENDING_SEARCH_SUGGESTIONS_CONFIG,
} from 'bundles/search-common/constants/autocomplete';
import { useEnterpriseAutocompleteSkillGroups } from 'bundles/search-common/hooks/useEnterpriseAutocompleteSkillGroups';
import useSearchBarShortcut from 'bundles/search-common/hooks/useSearchBarShortcut';
import { useQueryPrompts } from 'bundles/search-common/providers/QueryPromptsProvider';
import { useSearch } from 'bundles/search-common/providers/SearchContext';
import useSearchQuery from 'bundles/search-common/providers/useSearchQuery';
import { clearAllRecentSearches } from 'bundles/search-common/utils/SearchUtils';
import {
  type AutocompleteItem,
  type AutocompleteItemGroup,
  type AutocompleteItems,
  createAsYouTypeAutocompleteItems,
  createZeroStateAutocompleteItems,
  getTopProductSuggestionHits,
} from 'bundles/search-common/utils/autocomplete';
import useIsConsumerSerp from 'bundles/search-common/utils/useIsConsumerSerp';
import useIsEqp from 'bundles/search-common/utils/useIsEqp';
import inServerContext from 'bundles/ssr/util/inServerContext';
import { useCoachState } from 'bundles/unified-common/providers/CoachStateProvider';

import _t from 'i18n!nls/search-common';

type WrapperProps = {
  autocompleteId?: string;
  hideMobileSearchPage?: () => void;
  recentlySearchedItems?: AutocompleteItems;
  recentlyViewedItems?: AutocompleteItems;
  /** When set, shows the Recommendations Explainer T-Rex next to Recently viewed / learner favorites. */
  recommendationsExplainer?: RecommendationsExplainerInput;
  onRefreshItems?: () => void;
  placeholder?: string;
  disableTrendingProducts?: boolean;
  isQacGuidedProductEnabled?: boolean;
  enterpriseSearchBarConfig?: EnterpriseSearchBarConfig;
};

type Props = WrapperProps & {
  searchIsOpen: boolean;
  searchText: string;
  onInputChange: (value: string) => void;
  autocompleteMode: AutocompleteMode;
  onSubmit: (query: string, mergedItemsForTracking?: AutocompleteItems, selectedItem?: AutocompleteItem) => void;
  onDebouncedQueryChange?: (query: string) => void;
  // Two-panel search AI layout on LOHP (wider dropdown, taller input). Gated by the wrapper.
  isTwoPanelLohp?: boolean;
  isChatComposer?: boolean;
};

export const styles = {
  backdrop: css`
    /*
     * Rendered inside the page header so it sits within the header's stacking
     * context (z-index 3000) and dims both the header and the page below, while
     * the search bar / dropdown stay above it (local z-index 1001 > 1000).
     *
     * The header hides-on-scroll via a transform, which reparents this fixed
     * element's containing block to the (short) header box. We anchor above the
     * viewport (top: -100vh) and use an explicit oversized height — rather than
     * bottom: 0, which would measure from the short header box and only cover
     * part of the viewport — so the overlay fills the screen in both states.
     */
    position: fixed;
    top: -100vh;
    left: 0;
    right: 0;
    height: 300vh;
    background-color: var(--cds-color-utility-overlay);
    z-index: 1000;

    /* Hide on mobile/tablet - only show on desktop */
    @media (max-width: ${breakpoints.values.md - 1}px) {
      display: none;
    }
  `,
  autocomplete: (
    trendingTitle?: string,
    isConsumerSearchPage?: boolean,
    isEqp?: boolean,
    hasDegreeButton?: boolean,
    isTwoPanelLohp?: boolean
  ) => css`
    /* Ensure entire autocomplete component is above backdrop */
    position: relative;
    z-index: 1001;

    .cds-popoverDropdown-paper {
      /* if we don't remove 2px from CDS, we have an awkward gap with page content */
      margin-top: var(--cds-spacing-100);
      padding: var(--cds-spacing-200);
      scrollbar-width: none;
      -ms-overflow-style: none;

      &::-webkit-scrollbar {
        display: none;
      }

      /* Override any max-width constraints from CDS */
      max-width: none !important;

      /* Ensure consistent positioning regardless of input content */
      position: absolute !important;
      transform-origin: top left !important;

      /* Ensure dropdown appears above backdrop */
      z-index: 1001 !important;

      /* Responsive dropdown widths: 548px (sm–1280), 802px (1281–1699), 1056px (1700+) */
      @media (min-width: ${breakpoints.values.sm}px) and (max-width: 1280px) {
        width: 548px !important;
        ${
          isTwoPanelLohp &&
          css`
            transform: translateX(6%) !important;
          `
        }
      }

      @media (min-width: 1281px) and (max-width: 1699px) {
        width: 802px !important;
        ${
          isTwoPanelLohp &&
          css`
            transform: translateX(-12%) !important;
          `
        }
      }

      @media (min-width: 1700px) {
        width: 1056px !important;
        ${
          isTwoPanelLohp &&
          css`
            transform: translateX(-21%) !important;
          `
        }
      }
    }

    /* Add padding to listbox on mobile/tablet */
    @media (max-width: ${breakpoints.values.md - 1}px) {
      [role='listbox'] {
        padding: var(--cds-spacing-300) var(--cds-spacing-200) 0 var(--cds-spacing-200) !important;
      }
    }

    .cds-multiSelectGroup-label {
      padding: 0 !important;
      margin: 0 0 var(--cds-spacing-150) 0 !important;
    }

    /* Base styling for multiSelectGroup-list - remove padding from MuiButtonBase-root */
    .cds-multiSelectGroup-list {
      gap: var(--cds-spacing-150) !important;

      > div[role='option'] {
        &.MuiButtonBase-root,
        &[class*='MuiButtonBase-root'] {
          padding: 0 !important;
        }

        /* Show X button when option has CDS focused class */
        &.cds-AutocompleteOption-focused {
          .clear-icon-button {
            opacity: 1 !important;
          }
        }
      }
    }

    /* Also target MuiButtonBase-root directly within multiSelectGroup-list with higher specificity */
    .cds-multiSelectGroup-list div[role='option'][class*='MuiButtonBase-root'] {
      padding: 0 !important;
    }

    /* Remove overflow and spacing between prefix and content */
    .cds-AutocompleteOption-prefix {
      overflow: visible !important;
      margin-right: 0 !important;
      padding-right: 0 !important;
    }

    .cds-AutocompleteOption-content {
      overflow: visible !important;
      margin-left: 0 !important;
      padding-left: 0 !important;
    }

    /* Remove any gap between prefix and content */
    .MuiButtonBase-root {
      .cds-AutocompleteOption-prefix + .cds-AutocompleteOption-content {
        margin-left: 0 !important;
        padding-left: 0 !important;
      }
    }

    /* Style for Trending Topics Group */
    div[role='group']:has([data-group='${TRENDING_SEARCHES_GROUP_ID}']).cds-multiSelectGroup-list,
    div[role='group']:has([data-group='${ENTERPRISE_SKILLS_GROUP_ID}']).cds-multiSelectGroup-list {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      gap: var(--cds-spacing-100) !important;

      > div[role='option'] {
        padding: 0 !important;
        background-color: transparent !important;
        display: inline-block !important;
        width: auto !important;
        min-width: auto !important;
      }

      button.cds-button-secondary,
      .cds-button-secondary {
        border: 1px solid var(--cds-color-grey-100) !important;
        color: var(--cds-color-grey-975) !important;
        box-shadow: none !important;
      }
    }

    /* Style for Recently Viewed / Learner Favorites Group */
    div[role='group']:has([data-group='${RECENTLY_VIEWED_GROUP_ID}']).cds-multiSelectGroup-list,
    div[role='group']:has([data-group='${LEARNER_FAVORITES_GROUP_ID}']).cds-multiSelectGroup-list {
      display: flex;
      flex-direction: row;
      align-items: stretch; /* Make all children stretch to the same height */
      gap: var(--cds-spacing-100);

      > div[role='option'] {
        padding: 0 !important;
        background-color: transparent !important;
        display: flex !important;
        flex-direction: column;
        align-self: stretch; /* Ensure this element stretches to match parent height */

        /* Stretch MuiButtonBase-root - target the class directly */
        &.MuiButtonBase-root {
          align-self: stretch !important;
          display: flex !important;
          flex-direction: column;
          flex: 1 1 auto;
          min-height: 0; /* Allow flex children to shrink below content size */
        }

        .cds-AutocompleteOption-content {
          > div {
            flex: 1;
          }
        }

        /* Mobile (< sm, < 600px): width 128px */
        @media (max-width: ${breakpoints.values.md - 1}px) {
          margin-top: var(--cds-spacing-150);

          > div {
            padding-left: var(--cds-spacing-50) !important;
            width: 230px !important;
            max-width: 230px !important;
            display: flex;
            flex-direction: column;
            flex: 1;
          }
        }
      }

      /* Tablet and desktop (>= sm, >= 600px): width 246px */
      @media (min-width: ${breakpoints.values.md}px) {
        > div[role='option'] {
          width: 240px !important;
          min-width: 240px !important;
          max-width: 240px !important;

          > div {
            width: 240px !important;
            max-width: 240px !important;
            display: flex;
            flex-direction: column;
            flex: 1;
          }
        }
      }

      /* Mobile (< sm, < 600px): Make the section independently scrollable */
      @media (max-width: ${breakpoints.values.md - 1}px) {
        overflow-x: auto;
        overflow-y: hidden;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none; /* Firefox */
        -ms-overflow-style: none; /* IE and Edge */

        /* Prevent cards from shrinking */
        > div[role='option'] {
          flex-shrink: 0 !important;
        }

        /* Hide scrollbar for webkit browsers (Chrome, Safari, etc.) */
        &::-webkit-scrollbar {
          display: none;
        }
      }
    }

    /* Style for AI Prompts Zero State Group — pills wrap horizontally */
    div[role='group']:has([data-group='${AI_PROMPTS_ZERO_STATE_GROUP_ID}']).cds-multiSelectGroup-list {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      gap: var(--cds-spacing-100) !important;

      > div[role='option'] {
        padding: 0 !important;
        background-color: transparent !important;
        display: inline-block !important;
        width: auto !important;
        min-width: auto !important;

        /* Hide the suffix checkmark / any CDS chrome */
        .cds-AutocompleteOption-suffix {
          display: none !important;
        }
      }
    }

    /* Style for Autocomplete Group Labels */
    .cds-multiSelectGroup-label {
      ${typography2.subtitleMedium}
    }

    /* Reduce margin for Recently viewed and Learner favourites labels on mobile */
    .cds-multiSelectGroup-label:has(
      + div[role='group']:has([data-group='${RECENTLY_VIEWED_GROUP_ID}']).cds-multiSelectGroup-list
    ),
    .cds-multiSelectGroup-label:has(
      + div[role='group']:has([data-group='${LEARNER_FAVORITES_GROUP_ID}']).cds-multiSelectGroup-list
    ) {
      @media (max-width: ${breakpoints.values.md - 1}px) {
        margin-bottom: 0 !important;
      }
    }

    .clear-all-recent-searches-button {
      ${typography2.actionSecondary}
      background: none;
      border: none;
      cursor: pointer;
      padding: 0;
      margin-left: auto;
      padding-left: var(--cds-spacing-200);
      color: var(--cds-color-neutral-primary-weak) !important;

      &:hover {
        text-decoration: underline;
        background: none !important;
      }

      &:focus {
        outline: 2px solid var(--cds-color-blue-800);
        outline-offset: 2px;
        border-radius: var(--cds-border-radius-100);
      }
    }

    /* Visual separators - show space but hide the line */
    div[data-testid='visual-separator'],
    .cds-multiSelectGroup-divider {
      height: 20px;

      hr,
      .MuiDivider-root {
        background: none !important;
        padding: 0 !important;
        margin: 0 !important;
      }
    }

    &.cds-Search-base {
      margin: auto;
      margin-left: 4px;
      margin-top: 0;
      height: 44px;
      width: 100%;
      max-width: 480px;

      /* On phones the bar is full-width, so the 4px left nudge just indents it off the
         edge — drop it so it sits flush like the rest of the mobile content. */
      ${breakpoints.down('xs')} {
        margin-left: 0;
      }

      ${
        isConsumerSearchPage &&
        css`
          /* The SERP's bar stays full-width all the way up to md (see below), so the
             nudge would indent it past the leading edge the results share. */
          @media (max-width: ${breakpoints.values.md - 1}px) {
            margin-left: 0;
          }
        `
      }

      ${
        isTwoPanelLohp &&
        css`
          ${breakpoints.up('sm')} {
            height: 76px;

            .cds-Search-inputRoot {
              border-radius: var(--cds-border-radius-100);
              box-shadow: 0 0 20px -10px var(--cds-color-darken-500);
              align-items: unset;

              &::before {
                border-radius: var(--cds-border-radius-100) !important;
              }

              &:hover {
                background: var(--cds-color-white-0);
              }
            }
          }
        `
      }

      @media (max-width: 1023px) {
        margin-top: var(--cds-spacing-100);
        max-width: 100%;

        /* technically we show mobile header */
        ${
          !isEqp && !isConsumerSearchPage
            ? css`
                width: 100%;
                max-width: 100%;
                padding-left: var(--cds-spacing-100);
                padding-right: var(--cds-spacing-100);
                margin-top: var(--cds-spacing-50);
              `
            : css`
                width: 100%;
                max-width: 100%;
              `
        }
      }

      > div {
        border-radius: 24px;
        border: 1px solid var(--cds-color-grey-50);

        fieldset {
          /* remove duplicate border */
          border: none;
        }

        ::before {
          /* a11y border */
          border-radius: 24px !important;
        }
      }

      .cds-Search-prefix {
        display: none;
      }

      .cds-Search-suffix {
        /* for search button right spacing */
        padding-right: 4px;

        ${
          isTwoPanelLohp &&
          css`
            ${breakpoints.up('sm')} {
              align-items: end;
              padding-right: var(--cds-spacing-150);
              padding-bottom: var(--cds-spacing-150);
            }
          `
        }
      }

      /* necessary to remove inherited styles on account-profile (potentially elsewhere) */
      input {
        margin-bottom: 0;
        border: 0;
        box-shadow: none;
        background: transparent;
      }

      /* necessary to remove inherited styles on account-profile (potentially elsewhere) */
      input[aria-invalid='true']:invalid:focus {
        box-shadow: none;
      }
    }

    & .cds-AutocompleteOption-suffix {
      display: none !important;
    }

    & .cds-AutocompleteOption-checked:not(:hover) {
      background: transparent !important;
    }
  `,
  searchButton: css`
    /* circular shape of search button */
    border-radius: 24px;
  `,
  chatComposer: css`
    min-height: 100px;

    /* Gradient Border Rules */
    border-radius: var(--cds-border-radius-100);
    border: 1px solid transparent;
    background-image:
      linear-gradient(var(--cds-color-neutral-background-primary), var(--cds-color-neutral-background-primary)),
      linear-gradient(
        to right,
        var(--cds-color-interactive-primary),
        var(--cds-color-emphasis-primary-background-default)
      );
    background-origin: border-box;
    background-clip: padding-box, border-box;
    margin: 0 auto;

    &:focus-within {
      outline: 1px solid var(--cds-color-interactive-primary);
      outline-offset: 1px;
    }

    /* 1. Target the specific autocomplete popover class */
    &.search-autocomplete-dropdown,
    [role='tooltip'].search-autocomplete-dropdown {
      border: none !important;
      background: transparent !important;
    }

    .cds-Search-inputRoot {
      border: none !important;
      content: none;
      height: auto !important;

      .cds-input-input {
        padding-bottom: 70px;
        border-radius: var(--cds-border-radius-100) !important;
      }

      &:focus,
      &:hover {
        outline: none;
        border: none !important;
      }

      &::before {
        border: none !important;
        box-shadow: none !important;
      }
    }

    button[class*='cds-SearchClearButton-searchClearButton'] {
      position: absolute !important;
      top: var(--cds-spacing-50);
      right: var(--cds-spacing-25);
      left: auto !important;
    }

    .cds-inputAdornment-start {
      align-self: flex-end !important; /* Forces the icon wrapper to the bottom */
      margin-bottom: var(--cds-spacing-50) !important; /* Creates a clean gap from the bottom border */
      height: auto !important;
    }
  `,

  composerSubmitButton: css`
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: var(--cds-border-radius-100);
    background: var(--cds-color-interactive-primary);
    cursor: pointer;
    transition: background 120ms ease;

    &:hover:not(:disabled) {
      background: var(--cds-color-interactive-primary-hover);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  `,

  composerInputActionsButton: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-25);
    z-index: 2000; /* Ensure buttons are above the autocomplete dropdown */
  `,
};

const SearchBarContent = (props: Props) => {
  const {
    autocompleteId = 'search-autocomplete',
    searchIsOpen,
    hideMobileSearchPage,
    searchText,
    onSubmit,
    autocompleteMode,
    onInputChange,
    recentlySearchedItems = [],
    recentlyViewedItems = [],
    recommendationsExplainer,
    onRefreshItems,
    onDebouncedQueryChange,
    placeholder,
    disableTrendingProducts,
    isQacGuidedProductEnabled,
    enterpriseSearchBarConfig,
    isTwoPanelLohp,
    isChatComposer,
  } = props;

  const { isMessageLoading, isMessageStreaming } = useSelector((state: GlobalState) => ({
    isMessageLoading: state.behavior.messageLoader,
    isMessageStreaming: state.behavior.isMessageStreaming,
  }));
  const isCoachBusy = isMessageLoading || isMessageStreaming;

  const isMobileOrTabletView = useMediaQuery(breakpoints.down('sm'));

  const { setTriggerElementRef } = useCoachState();
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(!!searchIsOpen);
  const [hasDegreeButton, setHasDegreeButton] = React.useState(false);
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [autocompleteKey, setAutocompleteKey] = React.useState(0);
  const isRemovingItemRef = React.useRef(false);
  const skipNextInputChangeRef = React.useRef(false);
  const isConsumerSearchPage = useIsConsumerSerp();
  const isEqp = useIsEqp();
  const rootInputRef = React.useRef<HTMLInputElement>(null);
  const isProgrammaticOpenRef = React.useRef(false);
  const previousSearchTextRef = React.useRef<string>(searchText);
  const isUserLoggedIn = isAuthenticatedUser();
  const { prompts: queryPrompts } = useQueryPrompts();
  const location = typeof window !== 'undefined' ? window.location : null;
  const referrer = location?.search ? `${location.pathname}${location.search}` : location?.pathname || '';
  const { onboardingUrl } = useBuildOnboardingUrl({ referrer });
  // a separate, durable query results set for the "trending" section of the autocomplete
  const { results: trendingSuggestions, error: trendingItemsError } = useSearchQuery(
    TRENDING_SEARCH_SUGGESTIONS_CONFIG,
    {
      skip: inServerContext || !!disableTrendingProducts,
    }
  );
  const { results: trendingProducts, error: trendingProductsError } = useSearchQuery(TRENDING_SEARCH_RESULTS_CONFIG, {
    skip: inServerContext || recentlyViewedItems.length >= 4 || !!disableTrendingProducts,
  });
  // user input derived search results
  const { getSearchResults, updateQuery, isLoading, error: searchResultsError } = useSearch();
  const mainSearchData = getSearchResults(SEARCH_SUGGESTIONS_INDEX_ID);
  const aiSearchData = getSearchResults(SEARCH_MAIN_INDEX_ID);
  const { aiSearchMetadata } = aiSearchData || {};
  const isAISummaryEnabled = aiSearchMetadata?.isEligibleForAiGeneratedSummary && isConsumerSearchPage;
  const { elements: mainSearchDataElements, query: providerQuery, topProductSuggestion } = mainSearchData || {};
  const productHits = React.useMemo(
    () =>
      isQacGuidedProductEnabled && topProductSuggestion
        ? getTopProductSuggestionHits(topProductSuggestion)
        : aiSearchData?.elements,
    [isQacGuidedProductEnabled, topProductSuggestion, aiSearchData?.elements]
  );
  const productSource = isQacGuidedProductEnabled ? (topProductSuggestion?.source ?? 'PRODUCT_SEARCH') : undefined;
  const isLoadingOrHasErrors = isLoading || trendingItemsError || trendingProductsError || searchResultsError;

  // Detect presence of degrees link in header to prevent width overflow
  React.useEffect(() => {
    if (!inServerContext && isUserLoggedIn) {
      const degreeLink = document.querySelector('[href="/degrees"]');
      setHasDegreeButton(!!degreeLink);
    }
  }, [isUserLoggedIn]);

  React.useEffect(() => {
    setIsDropdownOpen(false);
  }, [isMobileOrTabletView]);

  // On EQP and consumer search pages, the search bar is inline (always visible),
  // so we don't need to programmatically open the autocomplete dropdown.
  // On other mobile pages, clicking the search button opens a full-page
  // search overlay, and we need to manually open the CDS Autocomplete popover.
  const isNotInlineSearchBar = !isEqp && !isConsumerSearchPage;

  React.useEffect(() => {
    // searchIsOpen is only used for mobile search button click - opens full-page search overlay
    if (isNotInlineSearchBar && searchIsOpen && isMobileOrTabletView) {
      setIsDropdownOpen(true);
      if (rootInputRef.current) {
        rootInputRef.current.focus();
      }
    }
    return undefined;
  }, [searchIsOpen, isNotInlineSearchBar, isMobileOrTabletView]);

  useSearchBarShortcut(rootInputRef);

  // For enterprise contexts with no zero-state content (no recent searches, no recently viewed,
  // trending disabled), suppress the dropdown on focus so it doesn't flash open empty.
  // 'input' trigger opens only when the user types, which is still correct for as-you-type.
  const shouldSuppressZeroStateDropdown =
    !!disableTrendingProducts && recentlySearchedItems.length === 0 && recentlyViewedItems.length === 0;

  const isSmTo1280 = useMediaQuery(`(min-width: ${breakpoints.values.sm}px) and (max-width: 1280px)`);
  const is1281To1699 = useMediaQuery('(min-width: 1281px) and (max-width: 1699px)');
  const is1700Plus = useMediaQuery('(min-width: 1700px)');

  // Dropdown width for CDS Popper (applied as inline style). Must match .cds-popoverDropdown-paper
  // breakpoints: 548px (sm–1280), 802px (1281–1699), 1056px (1700+).
  const dropdownWidth = React.useMemo(() => {
    if (is1700Plus) return '1056px';
    if (is1281To1699) return '802px';
    if (isSmTo1280) return '548px';
    return '548px';
  }, [isSmTo1280, is1281To1699, is1700Plus]);

  // Calculate screen width based on breakpoints for createZeroStateAutocompleteItems
  const screenWidth = React.useMemo(() => {
    if (is1700Plus) return 1700;
    if (is1281To1699) return 1281;
    if (isSmTo1280) return 1061;
    if (isMobileOrTabletView) return 1060;
    return undefined;
  }, [isMobileOrTabletView, isSmTo1280, is1281To1699, is1700Plus]);

  // Skills match the query the suggestions are showing, so they change together with them.
  const enterpriseSkillGroups = useEnterpriseAutocompleteSkillGroups(
    enterpriseSearchBarConfig,
    searchText,
    providerQuery ?? '',
    isDropdownOpen
  );

  // previous options - displayed during loading state
  const [prevOptions, setPrevOptions] = React.useState<AutocompleteItemGroup[]>([]);
  const autocompleteItemsInGroups = React.useMemo(() => {
    if (isLoadingOrHasErrors) return prevOptions;
    switch (autocompleteMode) {
      case AUTOCOMPLETE_MODES.AUTOCOMPLETE_ZERO_STATE:
        return createZeroStateAutocompleteItems({
          trendingSuggestions,
          isMobileOrTabletView,
          onboardingUrl,
          isUserLoggedIn,
          recentlySearchedItems,
          recentlyViewedItems,
          screenWidth,
          trendingProducts,
        });
      case AUTOCOMPLETE_MODES.AUTOCOMPLETE_AS_YOU_TYPE:
        return createAsYouTypeAutocompleteItems(
          mainSearchDataElements,
          onboardingUrl,
          isUserLoggedIn,
          productHits,
          searchText,
          queryPrompts,
          productSource,
          enterpriseSkillGroups
        );
      default:
        return [];
    }
  }, [
    autocompleteMode,
    mainSearchDataElements,
    trendingSuggestions,
    trendingProducts,
    prevOptions,
    isLoadingOrHasErrors,
    recentlyViewedItems,
    recentlySearchedItems,
    isMobileOrTabletView,
    onboardingUrl,
    isUserLoggedIn,
    screenWidth,
    productHits,
    productSource,
    searchText,
    queryPrompts,
    enterpriseSkillGroups,
  ]);
  const trendingGroup = autocompleteItemsInGroups.find((g) => g.id === TRENDING_SEARCHES_GROUP_ID);
  const trendingTitle = trendingGroup?.title;
  // Programmatically open dropdown after hydration when autocomplete items are available
  // This fixes the SSR hydration issue where focus happens before CDS event listeners attach
  // Only runs once on initial mount - if user has existing search text (e.g., from back button), skip auto-open
  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    // Don't auto-open if there's already search text (e.g., back button navigation)
    const hasExistingSearch = searchText && searchText.trim().length > 0;
    if (hasExistingSearch) return undefined;

    // Delay to ensure CDS Autocomplete is fully hydrated after SSR
    const HYDRATION_DELAY = 200;
    const BLUR_DELAY = 50;
    const RESET_DELAY = 300;

    const timers: NodeJS.Timeout[] = [];
    let isCancelled = false;

    const hydrationTimer = setTimeout(() => {
      if (isCancelled) return;

      const inputElement = rootInputRef.current || document.getElementById(`${autocompleteId}-input`);

      if (inputElement instanceof HTMLElement) {
        const isAlreadyFocused = document.activeElement === inputElement;

        if (isAlreadyFocused) {
          // Input is already focused from SSR, need to blur then refocus to trigger CDS focus event
          isProgrammaticOpenRef.current = true;
          inputElement.blur();

          const refocusTimer = setTimeout(() => {
            if (isCancelled) return;
            if (inputElement && document.body.contains(inputElement)) {
              inputElement.focus();
            }

            const resetTimer = setTimeout(() => {
              if (isCancelled) return;
              isProgrammaticOpenRef.current = false;
            }, RESET_DELAY);

            timers.push(resetTimer);
          }, BLUR_DELAY);

          timers.push(refocusTimer);
        }
      }
    }, HYDRATION_DELAY);

    timers.push(hydrationTimer);

    return () => {
      isCancelled = true;
      timers.forEach((timer) => clearTimeout(timer));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refocusAndOpenDropdown = React.useCallback<(clearVirtualFocus?: boolean) => void>(
    (clearVirtualFocus = false) => {
      // Immediately focus the input to prevent focus loss and keep dropdown open
      if (rootInputRef.current) {
        rootInputRef.current.focus();
      }

      // Clear virtual focus if needed (for keyboard navigation)
      if (clearVirtualFocus) {
        setTimeout(() => {
          if (rootInputRef.current) {
            const ariaActiveDescendant = rootInputRef.current.getAttribute('aria-activedescendant');
            if (ariaActiveDescendant) {
              rootInputRef.current.removeAttribute('aria-activedescendant');
            }
          }

          // Reset the flag after a short delay
          setTimeout(() => {
            isRemovingItemRef.current = false;
          }, 100);
        }, 50);
      } else {
        // Reset the flag after a short delay to allow any pending selection events to be skipped
        setTimeout(() => {
          isRemovingItemRef.current = false;
        }, 100);
      }
    },
    []
  );

  const handleRemoveRecentSearch = React.useCallback<() => void>(() => {
    isRemovingItemRef.current = true;
    setRefreshKey((prev) => prev + 1);
    onRefreshItems?.();
    refocusAndOpenDropdown(true);
  }, [onRefreshItems, refocusAndOpenDropdown]);

  const handleRemoveRecentlyViewed = React.useCallback<() => void>(() => {
    isRemovingItemRef.current = true;
    setRefreshKey((prev) => prev + 1);
    onRefreshItems?.();
    refocusAndOpenDropdown();
  }, [onRefreshItems, refocusAndOpenDropdown]);

  const handleClearAllRecentSearches = React.useCallback<() => void>(() => {
    isRemovingItemRef.current = true;
    clearAllRecentSearches();
    setRefreshKey((prev) => prev + 1);
    onRefreshItems?.();
    refocusAndOpenDropdown();
  }, [onRefreshItems, refocusAndOpenDropdown]);

  // Store updateQuery in ref to avoid recreating debounced function when it changes
  const updateQueryRef = React.useRef(updateQuery);
  React.useEffect(() => {
    updateQueryRef.current = updateQuery;
  }, [updateQuery]);

  // Store onDebouncedQueryChange in ref to avoid recreating debounced function when it changes
  const onDebouncedQueryChangeRef = React.useRef(onDebouncedQueryChange);
  React.useEffect(() => {
    onDebouncedQueryChangeRef.current = onDebouncedQueryChange;
  }, [onDebouncedQueryChange]);

  // Create debounced function once - it uses refs so it doesn't need to be recreated
  const debouncedRefineSearch = React.useMemo(
    () =>
      debounce((newValue: string) => {
        updateQueryRef.current(newValue);
        onDebouncedQueryChangeRef.current?.(newValue);
      }, DEBOUNCE_DURATION_MS),
    []
  );

  // Cleanup: cancel pending debounced calls on unmount
  React.useEffect(() => {
    return () => {
      debouncedRefineSearch.cancel();
    };
  }, [debouncedRefineSearch]);

  // When the response is loading, we want to persist the previous options
  React.useEffect(() => {
    if (!isLoadingOrHasErrors && !isEqual(autocompleteItemsInGroups, prevOptions)) {
      setPrevOptions(autocompleteItemsInGroups);
    }
  }, [autocompleteItemsInGroups, prevOptions, isLoadingOrHasErrors]);

  // Update the query in the provider when the search text changes
  React.useEffect(() => {
    if (!isLoadingOrHasErrors && searchText && searchText !== providerQuery) {
      debouncedRefineSearch(searchText);
    } else if (!searchText) {
      // When search is cleared, cancel pending debounce and immediately notify
      debouncedRefineSearch.cancel();
      onDebouncedQueryChangeRef.current?.('');
    }
  }, [searchText, debouncedRefineSearch, providerQuery, isLoadingOrHasErrors]);

  const handleSubmit = React.useCallback<
    (value: string, mergedItemsForTracking?: AutocompleteItems, selectedItem?: AutocompleteItem) => void
  >(
    (value: string, mergedItemsForTracking?: AutocompleteItems, selectedItem?: AutocompleteItem) => {
      onSubmit(decodeURIComponent(value), mergedItemsForTracking, selectedItem);
      // If we blur immediately, the text in the input will not update on selection
      setTimeout(() => {
        rootInputRef.current?.blur?.();
        setTriggerElementRef(rootInputRef.current);
      });
    },
    [onSubmit, setTriggerElementRef]
  );

  let prefixIcon;
  if (isAISummaryEnabled) {
    prefixIcon = <AIGenerateBrandedNewIcon aria-label={_t('AI Search')} />;
  }

  const handleTranscript = React.useCallback<(transcript: string) => void>(
    (transcript: string) => onInputChange(transcript),
    [onInputChange]
  );

  const handleEnterKeySubmit = React.useCallback<() => void>(() => {
    setIsDropdownOpen(false);
    if (isMobileOrTabletView) {
      setAutocompleteKey((prev) => prev + 1);
    }
    handleSubmit(encodeURIComponent(searchText));
  }, [handleSubmit, searchText, isMobileOrTabletView]);

  return (
    <div className="rc-SearchBarContent">
      {isDropdownOpen && (
        <div
          css={styles.backdrop}
          onClick={(e) => {
            e.stopPropagation();
            setIsDropdownOpen(false);
            hideMobileSearchPage?.();
          }}
          data-testid="search-autocomplete-backdrop"
          aria-hidden="true"
        />
      )}
      <Autocomplete
        key={autocompleteKey}
        popoverTrigger={shouldSuppressZeroStateDropdown ? 'input' : 'focus'}
        id={autocompleteId}
        name="query"
        placeholder={placeholder ?? _t('What do you want to learn?')}
        items={autocompleteItemsInGroups}
        value={searchText}
        allowsCustomValue
        defaultValue={searchText}
        prefixIcon={prefixIcon}
        css={[
          styles.autocomplete(trendingTitle, isConsumerSearchPage, isEqp, hasDegreeButton, isTwoPanelLohp),
          isChatComposer && !isMobileOrTabletView && styles.chatComposer,
        ]}
        inputActionButtons={
          isChatComposer ? (
            <div css={styles.composerInputActionsButton}>
              <SpeechToTextButton onTranscript={handleTranscript} size="small" />

              {isMobileOrTabletView ? (
                <IconButton intent="search" size="small" css={[styles.searchButton]} type="submit" />
              ) : (
                <IconButton
                  size="small"
                  tooltip=""
                  type="submit"
                  css={styles.composerSubmitButton}
                  aria-label={_t('Send message')}
                  icon={<ArrowUpIcon color="invert" />}
                />
              )}
            </div>
          ) : (
            <IconButton intent="search" size="small" css={[styles.searchButton]} type="submit" />
          )
        }
        onInputChange={(value) => {
          if (skipNextInputChangeRef.current) {
            skipNextInputChangeRef.current = false;
            onInputChange('');
            return;
          }
          // Store previous search text before updating
          previousSearchTextRef.current = searchText;
          onInputChange(value);
        }}
        disableSelectionChangeOnTab
        onSelectionChange={(selectedOptionText) => {
          setIsDropdownOpen(false);

          // Skip selection if we just removed an item
          if (isRemovingItemRef.current) {
            return;
          }
          if (selectedOptionText) {
            const selectedText = String(selectedOptionText);
            // Find the matching item to get its ID and merged items for proper tracking
            let itemId: string | undefined;
            let mergedItemsForTracking: AutocompleteItems | undefined;
            let matchedItem: AutocompleteItem | undefined;
            for (const group of autocompleteItemsInGroups) {
              const matchingIndex = group.children.findIndex(
                (item) => item.id === selectedText || item.textValue === selectedText || item.name === selectedText
              );
              if (matchingIndex !== -1) {
                const matchingItem = group.children[matchingIndex];
                if (matchingItem?.id) {
                  itemId = matchingItem.id;
                  matchedItem = { ...matchingItem, suggestionIndex: matchingIndex };
                  // For RECENTLY_VIEWED or LEARNER_FAVORITES groups, use the merged children (includes trending products)
                  // Also pass merged items for DIRECT_MATCHES items (trending products merged into these groups)
                  if (
                    group.id === RECENTLY_VIEWED_GROUP_ID ||
                    group.id === LEARNER_FAVORITES_GROUP_ID ||
                    matchingItem.id.startsWith(AUTOCOMPLETE_SECTION_PREFIXES.DIRECT_MATCHES)
                  ) {
                    mergedItemsForTracking = group.children;
                  }
                }
                break;
              }
            }

            // For CHAT_ONLY / Ask Anything selections that open the coach,
            // prevent CDS from filling the input with the prompt text.
            if (matchedItem?.routeType === 'CHAT_ONLY' || matchedItem?.routeType === 'coach_ask_anything') {
              skipNextInputChangeRef.current = true;
            }

            // Skill chips navigate away, so don't fill the input with their prefixed value.
            if (matchedItem?.id.startsWith(AUTOCOMPLETE_SECTION_PREFIXES.ENTERPRISE_SKILL)) {
              skipNextInputChangeRef.current = true;
            }

            // On mobile, remount the CDS Autocomplete after selection so that CDS's internal
            // focus-restoration (returnFocusRef) targets a detached node and is a no-op,
            // preventing the drawer from reopening.
            if (isMobileOrTabletView) {
              setAutocompleteKey((prev) => prev + 1);
            }

            // Pass the item ID (with prefix), merged items for tracking, and the matched item
            // to the wrapper — all routing decisions are handled in handleAutocompleteSubmit.
            handleSubmit(encodeURIComponent(itemId || selectedText), mergedItemsForTracking, matchedItem);
          }
        }}
        inputProps={{
          id: `${autocompleteId}-input`,
          'data-testid': 'search-autocomplete-input',
          name: 'query',
          type: 'text',
          onKeyUp: (e) => {
            if (e.key === 'Enter') {
              handleEnterKeySubmit();
            }
          },
        }}
        ref={rootInputRef}
        data-testid="search-autocomplete"
        aria-label={_t('Search catalog')}
        dropdownWidth={dropdownWidth}
        dropdownProps={{
          disablePortal: true,
          className: 'search-autocomplete-dropdown',
          style: {
            width: dropdownWidth,
            maxHeight: isTwoPanelLohp ? 'calc(85vh - 120px)' : 'calc(100vh - 120px)',
          },
        }}
        drawerInputProps={{
          enterkeyhint: 'Search',
          onKeyUp: (e) => {
            if (e.key === 'Enter') {
              handleEnterKeySubmit();
            }
          },
        }}
        onClose={() => {
          // Don't close if we're programmatically opening the dropdown (during blur/focus cycle)
          if (isProgrammaticOpenRef.current) {
            return;
          }

          // Don't close if focus is moving to a button inside the dropdown
          // This allows keyboard navigation to the X button via Right Arrow or clicking "Clear all"
          setTimeout(() => {
            const activeElement = document.activeElement;
            const isButtonInDropdown =
              activeElement &&
              (activeElement.getAttribute('data-testid') === 'clear-recent-search' ||
                activeElement.classList.contains('clear-all-recent-searches-button')) &&
              activeElement.closest('.search-autocomplete-dropdown');

            if (!isButtonInDropdown) {
              setIsDropdownOpen(false);
              hideMobileSearchPage?.();
            }
          }, 0);
        }}
        onOpen={() => {
          setIsDropdownOpen(true);
        }}
      >
        {({ title: groupTitle, children, id: groupId }) => {
          if (groupId === AI_PROMPTS_ZERO_STATE_GROUP_ID) {
            return (
              <Autocomplete.Group key={groupId} items={children} title={groupTitle} aria-label={groupTitle}>
                {children.map(({ id, name, routeType, intentType, routeParameter }, idx) => (
                  <Autocomplete.Option key={id} textValue={name} aria-label={name}>
                    <AutocompleteAiPromptOption
                      id={id}
                      name={name}
                      routeType={routeType}
                      intentType={intentType}
                      routeParameter={routeParameter}
                      suggestionIndex={idx}
                      groupId={groupId}
                      disabled={isCoachBusy}
                    />
                  </Autocomplete.Option>
                ))}
              </Autocomplete.Group>
            );
          }

          if (groupId === TRENDING_SEARCHES_GROUP_ID || groupId === ENTERPRISE_SKILLS_GROUP_ID) {
            return (
              <Autocomplete.Group key={groupId} items={children} title={groupTitle} aria-label={groupTitle}>
                {children.map(({ id, supportText, name, textValue, eventProductId }, idx) => (
                  <Autocomplete.Option
                    key={name}
                    supportText={supportText}
                    textValue={textValue ?? name}
                    aria-label={name}
                  >
                    <AutocompleteTrendingOption
                      autocompleteState={autocompleteMode}
                      id={id}
                      recentlyViewedItems={recentlyViewedItems}
                      suggestionIndex={idx}
                      groupId={groupId}
                      eventProductId={eventProductId}
                      eventProductName={name}
                    >
                      {name}
                    </AutocompleteTrendingOption>
                  </Autocomplete.Option>
                ))}
              </Autocomplete.Group>
            );
          }

          if (groupId === ONBOARDING_GROUP_ID) {
            const onboardingItem = children[0];
            if (!onboardingItem) return null;

            return (
              <Autocomplete.Group key={groupId} items={children} title="" aria-label={groupTitle}>
                <Autocomplete.Option
                  key={onboardingItem.id}
                  textValue={onboardingItem.textValue ?? onboardingItem.name}
                  aria-label={onboardingItem.name}
                >
                  <AutocompleteOnboardingOption
                    autocompleteState={autocompleteMode}
                    id={onboardingItem.id}
                    recentlyViewedItems={recentlyViewedItems}
                    suggestionIndex={0}
                    path={onboardingItem.path}
                    groupId={groupId}
                  />
                </Autocomplete.Option>
              </Autocomplete.Group>
            );
          }

          if (groupId === RECENTLY_VIEWED_GROUP_ID || groupId === LEARNER_FAVORITES_GROUP_ID) {
            const productCards = children
              .map((item, idx) => {
                return (
                  <Autocomplete.Option key={item.name} textValue={item.name} aria-label={item.name}>
                    <AutocompleteSearchProductCard
                      autocompleteState={autocompleteMode}
                      id={item.id || ''}
                      recentlyViewedItems={children}
                      suggestionIndex={idx}
                      item={item}
                      groupId={groupId}
                      onRemove={handleRemoveRecentlyViewed}
                    />
                  </Autocomplete.Option>
                );
              })
              .filter((card): card is React.ReactElement => card !== null);

            return (
              <Autocomplete.Group
                key={
                  groupId === RECENTLY_VIEWED_GROUP_ID || groupId === LEARNER_FAVORITES_GROUP_ID
                    ? `${groupId}-${refreshKey}`
                    : groupId
                }
                items={children}
                // @ts-expect-error: Autocomplete.Group title prop expects string, but we need TitleRow for the explainer icon
                title={
                  <RecommendationsExplainerTitleRow recommendationsExplainer={recommendationsExplainer}>
                    <span>{groupTitle}</span>
                  </RecommendationsExplainerTitleRow>
                }
                aria-label={groupTitle}
              >
                {productCards}
              </Autocomplete.Group>
            );
          }

          return (
            <Autocomplete.Group
              key={groupId === RECENT_SEARCHES_GROUP_ID ? `${groupId}-${refreshKey}` : groupId}
              items={children}
              // @ts-expect-error: Autocomplete.Group title prop expects string, but we need to render a button alongside the title
              title={
                groupId === RECENT_SEARCHES_GROUP_ID ? (
                  <div
                    css={css`
                      display: flex;
                      justify-content: space-between;
                      align-items: center;
                      width: 100%;
                    `}
                  >
                    <span>{groupTitle}</span>
                    <Button
                      variant="ghostSecondary"
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        e.preventDefault();
                        e.nativeEvent.stopImmediatePropagation();
                        handleClearAllRecentSearches();
                      }}
                      onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.stopPropagation();
                          e.preventDefault();
                          e.nativeEvent.stopImmediatePropagation();
                          handleClearAllRecentSearches();
                        }
                      }}
                      onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        e.preventDefault();
                        e.nativeEvent.stopImmediatePropagation();
                      }}
                      onMouseUp={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        e.preventDefault();
                        e.nativeEvent.stopImmediatePropagation();
                      }}
                      onPointerDown={(e: React.PointerEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        e.preventDefault();
                        e.nativeEvent.stopImmediatePropagation();
                      }}
                      tabIndex={0}
                      className="clear-all-recent-searches-button"
                      aria-label={_t('Clear all recent searches')}
                    >
                      {_t('Clear all')}
                    </Button>
                  </div>
                ) : (
                  groupTitle
                )
              }
            >
              {children.map(
                ({ imageUrl, id, supportText, name, textValue, isAiPrompt, routeType, intentType }, idx) => {
                  return (
                    <Autocomplete.Option
                      prefix={
                        <AutocompleteItemIcon
                          imageUrl={imageUrl}
                          groupId={groupId}
                          avatarAltText={name}
                          isAiPrompt={isAiPrompt}
                        />
                      }
                      key={name}
                      supportText={supportText}
                      textValue={textValue ?? name}
                      aria-label={name}
                    >
                      {groupId === RECENT_SEARCHES_GROUP_ID ? (
                        <AutocompleteRecentlySearchedOption
                          autocompleteState={autocompleteMode}
                          id={id}
                          recentlyViewedItems={recentlyViewedItems}
                          suggestionIndex={idx}
                          onRemove={handleRemoveRecentSearch}
                          inputRef={rootInputRef}
                        >
                          {name}
                        </AutocompleteRecentlySearchedOption>
                      ) : (
                        <AutocompleteOptionTrackingDiv
                          autocompleteState={autocompleteMode}
                          id={id}
                          recentlyViewedItems={recentlyViewedItems}
                          suggestionIndex={idx}
                          searchText={providerQuery}
                          productItem={groupId === DIRECT_MATCHES_GROUP_ID ? children[idx] : undefined}
                        >
                          {name}
                        </AutocompleteOptionTrackingDiv>
                      )}
                    </Autocomplete.Option>
                  );
                }
              )}
            </Autocomplete.Group>
          );
        }}
      </Autocomplete>
    </div>
  );
};

export default SearchBarContent;
